import { useMemo, useRef, useCallback, useEffect } from "react";
import { resolveOsbDims } from "../../utils/osbDims";
import { shareApplication } from "../../utils/insulationApplications";

const MAX_ANALOG_PRICE_AGE_DAYS = 14;
const GIK_MSK_SOURCE = "ПРАЙС ГИК МСК";
const PAVING_ANALOG_CATEGORIES = new Set(["Бордюр", "Плитка тротуарная"]);

const IZOLIFE_SOURCE_PREFIX = 'ПРАЙС ИЗОЛАЙФ';

const isIzolifeSource = (source) =>
  source && String(source).toUpperCase().includes(IZOLIFE_SOURCE_PREFIX.toUpperCase());

const extractRegionFromIzolifeSource = (source) => {
  const upper = String(source || '').toUpperCase();
  if (upper.includes('СПБ')) return 'СПБ';
  if (upper.includes('МСК')) return 'МСК';
  if (upper.includes('КРД')) return 'КРД';
  return null;
};

const parseNumIzo = (v) => {
  const n = parseFloat(String(v || '0').replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const calcIzolifePrice = (data, name, requiredPackages, priceValue, cache) => {
  const source = data.SOURCE;
  const region = extractRegionFromIzolifeSource(source);

  const allSettings = cache?.izolifeSettings?.settings || [];
  const izoSettings = allSettings.find(s => s.REGION === region) || {};
  const palletPrice = parseNumIzo(izoSettings.PALLET_PRICE);
  const phrase = izoSettings.PHRASE || '';

  const numberPerPallet = parseNumIzo(data.UNITS_PER_PALLET || data.NUMBER_PER_PALLET) || 1;
  const truckPallets = parseNumIzo(cache?.izolifeSettings?.truckPalletsByProduct?.[name]) || 0;

  // 1. Сколько нужно паллет для требуемого количества упаковок
  const requiredPallets = Math.ceil(requiredPackages / numberPerPallet);

  // 2. Сколько всего паллет будет с учетом правил округления
  let actualPallets;

  if (palletPrice) {
    // Если есть palletPrice - округляем до целых поддонов (без учета фуры)
    actualPallets = requiredPallets;
  } else if (truckPallets > 0) {
    // Если нет palletPrice, но есть truckPallets - округляем до полной фуры
    const requiredTrucks = Math.ceil(requiredPallets / truckPallets);
    actualPallets = requiredTrucks * truckPallets;
  } else {
    // Если нет ни palletPrice, ни truckPallets - берем как есть
    actualPallets = requiredPallets;
  }

  // 3. Сколько всего упаковок будет
  const actualPackages = actualPallets * numberPerPallet;

  console.log('📦 [calcIzolifePrice]:', {
    requiredPackages,
    numberPerPallet,
    requiredPallets,
    palletPrice: palletPrice ? 'есть' : 'нет',
    truckPallets,
    actualPallets,
    actualPackages,
  });

  // 4. Комментарий, если берется больше, чем нужно
  const comment = requiredPackages !== actualPackages
    ? `Нужно ${requiredPackages} упаковок, но до полной погрузки берется ${actualPackages}`
    : null;

  // 5. Цена за упаковку (без доставки)
  const pricePerPackageWithDelivery = priceValue;

  console.log(`🟡 [Изолайф Products] результат для "${name}":`, {
    requiredPackages,
    requiredPallets,
    truckPallets,
    actualPallets,
    actualPackages,
    pricePerPackageWithDelivery: Math.round(pricePerPackageWithDelivery),
    comment,
  });

  return {
    pricePerPackageWithDelivery,
    actualPackages,
    comment,
    phrase,
  };
};
const parsePriceUpdateDate = (dateValue) => {
  if (!dateValue || typeof dateValue !== "string") return null;
  const trimmed = dateValue.trim();
  const match = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isPriceFreshEnoughForAnalog = (dateValue, source) => {
  const parsedDate = parsePriceUpdateDate(dateValue);
  if (!parsedDate) return true;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsedDate.setHours(0, 0, 0, 0);

  const diffMs = today.getTime() - parsedDate.getTime();
  if (diffMs < 0) return true;

  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  return diffDays <= MAX_ANALOG_PRICE_AGE_DAYS;
};

const normalizeValue = (value) => String(value || "").trim();

// --- Аналоги кирпича ---
const isBrickCategory = (category) =>
  normalizeValue(category).toLowerCase().startsWith("кирпич");

// Прочность кирпича хранится грейдом "М###" (М100..М400). Достаём число для сравнения.
const parseStrengthGrade = (value) => {
  const digits = String(value || "").replace(/[^\d]/g, "");
  return digits ? parseInt(digits, 10) : 0;
};

// Нормализация текстовой характеристики (НФ, пустотность) для сравнения.
const normalizeBrickAttr = (value) => normalizeValue(value).toLowerCase();

const isGikMskPavingCategory = (data) => {
  const source = normalizeValue(data?.SOURCE).toUpperCase();
  const category = normalizeValue(data?.CATEGORY);
  return source === GIK_MSK_SOURCE && PAVING_ANALOG_CATEGORIES.has(category);
};

const useAnalogs = (products, options, paymentMethod, cache) => {
  const analogsCache = useRef({});

  // Очистка кэша при изменении options, paymentMethod или cache (включая izolifeSettings)
  useEffect(() => {
    console.log('🔍 useAnalogs: Clearing cache due to options, paymentMethod or cache change', { paymentMethod });
    analogsCache.current = {};
  }, [options, paymentMethod, cache]);

  const filteredOptions = useMemo(() => {
    return Object.fromEntries(
      Object.entries(options).filter(
        ([_, data]) =>
          (
            data.CATEGORY?.includes("Утеплитель") ||
            data.CATEGORY?.includes("OSB") ||
            data.CATEGORY?.includes("Экструдированный пенополистирол") ||
            data.CATEGORY?.includes("Звукоизоляция")
          ) &&
          isPriceFreshEnoughForAnalog(data.PRICE_UPDATE_DATE, data.SOURCE)
      )
    );
  }, [options]);

  const getAnalogs = useCallback(
    (productName, productQuantity) => {
      console.log(`🔍 getAnalogs called with: productName=${productName}, productQuantity=${productQuantity}, paymentMethod=${paymentMethod}`);
      // Проверка и установка значения 100 по умолчанию для productQuantity
      let parsedQuantity;
      if (productQuantity == null || productQuantity === '' || productQuantity === 0 || isNaN(productQuantity)) {
        parsedQuantity = 100;
      } else {
        parsedQuantity = parseFloat(productQuantity) || 1;
      }
      const cacheKey = `${productName}_${parsedQuantity}_${paymentMethod}`;
      // Используем разные варианты кэша для разных наборов данных
      const optionsSignature = Object.keys(options).slice(0, 3).sort().join('|');
      const fullCacheKey = `${cacheKey}_${optionsSignature}`;

      if (analogsCache.current[fullCacheKey]) return analogsCache.current[fullCacheKey];
      if (!productName || !options[productName]) return { currentItem: null, analogs: [] };

      console.log(`🟡 [Изолайф Products] cache.izolifeSettings при вызове getAnalogs("${productName}"):`, {
        settingsCount: cache?.izolifeSettings?.settings?.length ?? 'нет',
        deliveryCostRegions: Object.keys(cache?.izolifeSettings?.deliveryCostByRegion || {}),
        truckPalletsKeys: Object.keys(cache?.izolifeSettings?.truckPalletsByProduct || {}),
      });

      const currentData = options[productName];
      const currentDensity = currentData.DENSITY ? parseFloat(currentData.DENSITY) || 0 : 0;
      const currentInsulationThickness = currentData.INSULATION_THICKNESS
        ? parseFloat(currentData.INSULATION_THICKNESS) || 0
        : 0;
      const currentInsulationThicknessOSB = currentData.OSB_THICKNESS_MM
        ? parseFloat(currentData.OSB_THICKNESS_MM) || 0
        : 0;
      const currentCategory = currentData.CATEGORY || "-";
      const currentAnalogy = currentData.ANALOGY;
      const currentSheetsInsulation = currentData.SHEETS_INSULATION
        ? parseInt(currentData.SHEETS_INSULATION) || 0
        : 0;
      const currentArea = currentData.AREA_SQUARE
        ? parseFloat(String(currentData.AREA_SQUARE).replace(",", ".")) || 0
        : 0;
      const currentAreaOSB = currentData.AREA_OSB
        ? parseFloat(String(currentData.AREA_OSB).replace(",", ".")) || 0
        : 0;
      const currentManufacturer = currentData.MANUFACTURER || "Не указан";
      const currentCompression = currentData.COMPRESSION || null;
      const currentPavingAnalogId = normalizeValue(currentData.PAVING_ANALOG_ID);
      const currentPrice =
        paymentMethod === "cash" ? currentData.PRICE_CASH : currentData.PRICE_NONCASH;
      const currentPriceValue = currentPrice ? parseFloat(currentPrice) || 0 : 0;
      const isCurrentPriceOnRequest = !currentPriceValue || currentPriceValue <= 0;
      const isPavingAnalogMode =
        isGikMskPavingCategory(currentData) && currentPavingAnalogId !== "";

      if (isGikMskPavingCategory(currentData)) {
        console.log('[PAVING_ANALOGS] current product lookup:', {
          productName,
          category: currentCategory,
          source: currentData.SOURCE,
          manufacturer: currentManufacturer,
          pavingAnalogId: currentPavingAnalogId,
          hasMode: isPavingAnalogMode,
        });
      }

      console.log(`🔍 useAnalogs getAnalogs (${productName}):`, {
        paymentMethod,
        cashPrice: currentData.PRICE_CASH,
        nonCashPrice: currentData.PRICE_NONCASH,
        selectedPrice: currentPrice,
        isCurrentPriceOnRequest
      });

      const isOsb = currentCategory.includes("OSB");
      const isUteplitel = currentCategory.includes("Утеплитель") && productName.toLowerCase().includes('утеплитель');
      const isExtrudedPolystyrene = productName.toLowerCase().includes("экструдированный пенополистирол");
      const isBrick = isBrickCategory(currentCategory);

      // Характеристики текущего кирпича (НФ / пустотность / прочность)
      const currentBrickFormat = normalizeBrickAttr(currentData.FORMAT);
      const currentBrickVoidness = normalizeBrickAttr(currentData.DENSITY_REPEAT);
      const currentBrickStrengthGrade = parseStrengthGrade(currentData.STRENGTH);

      // Проверяем, что это подходящая категория для аналогов
      if (!isPavingAnalogMode && !isOsb && !isUteplitel && !isExtrudedPolystyrene && !isBrick) {
        return { currentItem: null, analogs: [], analogsOnRequest: [] };
      }

      // KAL-43: для ОСБ добираем толщину/площадь из названия, если в справочнике пусто.
      const currentOsbDims = isOsb ? resolveOsbDims(productName, currentData) : { thickness: 0, area: 0 };
      const resolvedThicknessOSB = isOsb ? currentOsbDims.thickness : currentInsulationThicknessOSB;
      const resolvedAreaOSB = isOsb ? currentOsbDims.area : currentAreaOSB;

      const effectiveArea = isOsb ? resolvedAreaOSB : currentArea;
      const effectiveThickness = isOsb ? resolvedThicknessOSB : currentInsulationThickness;
      const requiredVolume = parsedQuantity > 0 ? parsedQuantity * effectiveArea : 1 * effectiveArea;

      const currentItem = {
        value: productName,
        label: productName,
        details: {
          price: isCurrentPriceOnRequest ? null : currentPrice,
          area: effectiveArea || "Нет данных",
          insulationThickness: effectiveThickness.toFixed(2) || "0.00",
          category: currentCategory || "-",
          sheetsInsulation: isOsb ? parsedQuantity : currentSheetsInsulation || "Нет данных",
          manufacturer: currentManufacturer,
          compression: currentCompression,
          density: (isUteplitel || isExtrudedPolystyrene) ? currentDensity : undefined,
        },
        requiredVolume,
        parsedQuantity,
        pricePerPackage: currentPriceValue,
        pricePerSquareMeter: effectiveArea > 0 && currentPriceValue > 0 ? currentPriceValue / effectiveArea : 0,
        pricePerSheet: isOsb
          ? currentPriceValue
          : currentSheetsInsulation > 0 && currentPriceValue > 0
            ? currentPriceValue / currentSheetsInsulation
            : 0,
        isPriceOnRequest: isCurrentPriceOnRequest,
      };

      // Определяем диапазон плотности для утеплителей
      let densityRange = 7; // Значение по умолчанию, если cache.densityRanges пуст или не найден
      if (isUteplitel && !isExtrudedPolystyrene && cache?.densityRanges?.length > 0) {
        // Находим подходящий диапазон из cache.densityRanges
        const densityRangeData = cache.densityRanges.find(range =>
          currentDensity >= range.densityFrom &&
          (range.densityTo === null || currentDensity <= range.densityTo)
        );
        densityRange = densityRangeData ? densityRangeData.range : 7;
      }

      let analogs = [];
      let analogsOnRequest = [];

      if (isPavingAnalogMode) {
        const pavingFiltered = Object.entries(options).filter(([name, data]) => {
          if (name === productName) return false;
          if (!isGikMskPavingCategory(data)) return false;

          const analogId = normalizeValue(data.PAVING_ANALOG_ID);
          return analogId !== "" && analogId === currentPavingAnalogId;
        });

        const withPrice = pavingFiltered.filter(([_, data]) => {
          const price = paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH;
          return parseFloat(price) > 0;
        });
        const withoutPrice = pavingFiltered.filter(([_, data]) => {
          const price = paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH;
          return !price || parseFloat(price) <= 0;
        });

        analogs = withPrice
          .map(([name, data]) => {
            const price = paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH;
            const priceValue = price ? parseFloat(price) || 0 : 0;
            const priceDifferencePerPackage = isCurrentPriceOnRequest ? 0 : priceValue - currentItem.pricePerPackage;

            return {
              value: name,
              label: name,
              details: {
                price,
                area: "Нет данных",
                insulationThickness: "0.00",
                category: data.CATEGORY || "-",
                manufacturer: data.MANUFACTURER || "Не указан",
                compression: data.COMPRESSION || null,
              },
              requiredSheets: parsedQuantity,
              analogPricePerPackage: priceValue,
              priceDifferencePerPackage,
              analogPricePerSquareMeter: priceValue,
              priceDifferencePerSquareMeter: priceDifferencePerPackage,
              analogPricePerSheet: priceValue,
              priceDifferencePerSheet: priceDifferencePerPackage,
              volumeBenefit: 0,
              totalCost: priceValue * parsedQuantity,
              isPavingAnalog: true,
              pavingAnalogId: currentPavingAnalogId,
            };
          })
          .sort((a, b) => {
            if (isCurrentPriceOnRequest) {
              return a.label.localeCompare(b.label, 'ru');
            }
            return a.analogPricePerPackage - b.analogPricePerPackage;
          });

        console.log('[PAVING_ANALOGS] matched analogs:', {
          productName,
          pavingAnalogId: currentPavingAnalogId,
          totalMatched: pavingFiltered.length,
          withPrice: withPrice.length,
          withoutPrice: withoutPrice.length,
          matchedNames: pavingFiltered.map(([name, data]) => ({
            name,
            manufacturer: data.MANUFACTURER || null,
            pavingAnalogId: data.PAVING_ANALOG_ID || null,
            source: data.SOURCE || null,
          })),
        });

        analogsOnRequest = withoutPrice
          .map(([name, data]) => ({
            value: name,
            label: name,
            details: {
              price: null,
              area: "Нет данных",
              insulationThickness: "0.00",
              category: data.CATEGORY || "-",
              manufacturer: data.MANUFACTURER || "Не указан",
              compression: data.COMPRESSION || null,
            },
            requiredSheets: parsedQuantity,
            isPriceOnRequest: true,
            isPavingAnalog: true,
            pavingAnalogId: currentPavingAnalogId,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
      } else if (isOsb) {
        const osbFiltered = Object.entries(filteredOptions)
          .filter(([name, data]) => {
            if (name === productName) return false;
            const { thickness: insulationThicknessOSB, area: areaOsb } = resolveOsbDims(name, data);
            const category = data.CATEGORY || "-";
            return (
              areaOsb > 0 &&
              Math.abs(insulationThicknessOSB - resolvedThicknessOSB) < 0.01 &&
              category.includes("OSB")
            );
          });

        // Разделяем на товары с ценой и без
        const withPrice = osbFiltered.filter(([name, data]) => {
          const price = paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH;
          return parseFloat(price) > 0;
        });
        const withoutPrice = osbFiltered.filter(([name, data]) => {
          const price = paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH;
          return !price || parseFloat(price) <= 0;
        });

        console.log(`🟡 [Изолайф Products OSB] аналогов с ценой: ${withPrice.length}, из них Изолайф: ${withPrice.filter(([n, d]) => isIzolifeSource(d.SOURCE)).length}`);

        analogs = withPrice
          .map(([name, data]) => {
            const { thickness: thicknessOsbResolved, area: areaOsb } = resolveOsbDims(name, data);
            const price =
              paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH;
            const priceValue = price ? parseFloat(price) || 0 : 0;
            const requiredSheets = Math.ceil(currentItem.requiredVolume / areaOsb);

            console.log(`🟡 [Изолайф Products OSB] аналог "${name}": SOURCE="${data.SOURCE}", isIzolife=${isIzolifeSource(data.SOURCE)}`);
            if (isIzolifeSource(data.SOURCE)) {
              const izoResult = calcIzolifePrice(data, name, requiredSheets, priceValue, cache);
              const effectivePrice = izoResult.pricePerPackageWithDelivery;
              const analogPricePerSquareMeter = areaOsb > 0 ? effectivePrice / areaOsb : 0;
              const priceDifferencePerSquareMeter = isCurrentPriceOnRequest ? 0 : analogPricePerSquareMeter - currentItem.pricePerSquareMeter;
              const analogPricePerSheet = effectivePrice;
              const priceDifferencePerSheet = isCurrentPriceOnRequest ? 0 : analogPricePerSheet - currentItem.pricePerSheet;
              const totalCost = effectivePrice * izoResult.actualPackages;
              const currentTotalCost = currentItem.pricePerPackage * parsedQuantity;
              const volumeBenefit = isCurrentPriceOnRequest ? -1 : totalCost - currentTotalCost;
              return {
                value: name,
                label: name,
                details: {
                  price: String(Math.round(effectivePrice)),
                  area: areaOsb,
                  insulationThickness: thicknessOsbResolved || "0.00",
                  category: data.CATEGORY || "-",
                  manufacturer: data.MANUFACTURER || "Не указан",
                  compression: data.COMPRESSION || null,
                },
                requiredSheets: izoResult.actualPackages,
                analogPricePerSheet,
                priceDifferencePerSheet,
                analogPricePerSquareMeter,
                priceDifferencePerSquareMeter,
                volumeBenefit,
                totalCost,
                isIzolife: true,
                izolifeComment: izoResult.comment,
                izolifePhrase: izoResult.phrase,
              };
            }

            const analogPricePerSquareMeter = priceValue / areaOsb;
            const priceDifferencePerSquareMeter = isCurrentPriceOnRequest
              ? 0
              : analogPricePerSquareMeter - currentItem.pricePerSquareMeter;
            const analogPricePerSheet = priceValue;
            const priceDifferencePerSheet = isCurrentPriceOnRequest
              ? 0
              : analogPricePerSheet - currentItem.pricePerSheet;
            const analogSquareMeters = analogPricePerSquareMeter * (requiredSheets * areaOsb);
            const currentSquareMetersCost = currentItem.pricePerSquareMeter * currentItem.requiredVolume;
            const volumeBenefit = isCurrentPriceOnRequest
              ? -1 // Для сортировки - все аналоги "выгодны" если текущий без цены
              : analogSquareMeters - currentSquareMetersCost;
            const totalCost = priceValue * requiredSheets;

            return {
              value: name,
              label: name,
              details: {
                price,
                area: areaOsb,
                insulationThickness: thicknessOsbResolved || "0.00",
                category: data.CATEGORY || "-",
                manufacturer: data.MANUFACTURER || "Не указан",
                compression: data.COMPRESSION || null,
              },
              requiredSheets,
              analogPricePerSheet,
              priceDifferencePerSheet,
              analogPricePerSquareMeter,
              priceDifferencePerSquareMeter,
              volumeBenefit,
              totalCost,
            };
          })
          .filter((analog) => {
            if (isCurrentPriceOnRequest) {
              // Если текущий товар без цены - показываем все аналоги с ценой
              return analog !== null;
            }
            // Иначе - только те, что дешевле
            return analog && analog.analogPricePerSquareMeter < currentItem.pricePerSquareMeter;
          })
          .sort((a, b) => {
            if (isCurrentPriceOnRequest) {
              // Сортируем по итоговой цене (от дешёвых)
              return a.totalCost - b.totalCost;
            }
            return a.analogPricePerSquareMeter - b.analogPricePerSquareMeter;
          })
          ;

        // Товары "по запросу" для OSB
        analogsOnRequest = withoutPrice
          .map(([name, data]) => {
            const { thickness: thicknessOsbResolved, area: areaOsb } = resolveOsbDims(name, data);
            const requiredSheets = Math.ceil(currentItem.requiredVolume / areaOsb);

            return {
              value: name,
              label: name,
              details: {
                price: null,
                area: areaOsb,
                insulationThickness: thicknessOsbResolved || "0.00",
                category: data.CATEGORY || "-",
                manufacturer: data.MANUFACTURER || "Не указан",
                compression: data.COMPRESSION || null,
              },
              requiredSheets,
              isPriceOnRequest: true,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label, 'ru'))
          ;
      } else if (isExtrudedPolystyrene) {
        const xpsFiltered = Object.entries(filteredOptions)
          .filter(([name, data]) => {
            if (name === productName) return false;
            const insulationThickness = parseFloat(data.INSULATION_THICKNESS || 0);
            const compression = parseFloat(data.COMPRESSION || 0);
            const category = data.CATEGORY || "-";
            const isSameInsulationThickness =
              insulationThickness > 0 &&
              Math.abs(insulationThickness - currentInsulationThickness) < 0.01;
            const isUteplitelCategory = category.includes("Утеплитель") || category.includes("Экструдированный пенополистирол");
            const currentCompressionValue = parseFloat(currentCompression || 0);

            // Проверка диапазона compression на основе данных из БД
            let isCompressionInRange = compression >= (currentCompressionValue - 25); // fallback значение
            if (cache?.compressionRanges?.length > 0) {
              // Ищем подходящий диапазон в БД
              const compressionRange = cache.compressionRanges.find(range => {
                const fromValue = range.fromValue;
                const toValue = range.toValue;
                const compressionDiff = compression - currentCompressionValue;

                if (toValue === null || toValue === '') {
                  // Если верхняя граница null, проверяем только нижнюю
                  return compressionDiff >= fromValue;
                } else {
                  // Если есть обе границы, проверяем диапазон
                  return compressionDiff >= fromValue && compressionDiff <= toValue;
                }
              });

              // Если нашли подходящий диапазон, значит compression в допустимых пределах
              isCompressionInRange = compressionRange !== undefined;
            }

            return (
              name.toLowerCase().includes("экструдированный пенополистирол") &&
              isSameInsulationThickness &&
              isUteplitelCategory &&
              isCompressionInRange
            );
          });

        // Разделяем на товары с ценой и без
        const withPrice = xpsFiltered.filter(([name, data]) => {
          const price = paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH;
          return parseFloat(price) > 0;
        });
        const withoutPrice = xpsFiltered.filter(([name, data]) => {
          const price = paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH;
          return !price || parseFloat(price) <= 0;
        });

        console.log(`🟡 [Изолайф Products XPS] аналогов с ценой: ${withPrice.length}, из них Изолайф: ${withPrice.filter(([n, d]) => isIzolifeSource(d.SOURCE)).length}`);

        analogs = withPrice
          .map(([name, data]) => {
            const area = parseFloat(String(data.AREA_SQUARE)?.replace(",", ".") || 0);
            const sheetsInsulation = parseInt(data.SHEETS_INSULATION || 0);
            const price =
              paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH;
            const priceValue = price ? parseFloat(price) || 0 : 0;
            const requiredPackages = Math.ceil(currentItem.requiredVolume / area);

            console.log(`🟡 [Изолайф Products] аналог "${name}": SOURCE="${data.SOURCE}", isIzolife=${isIzolifeSource(data.SOURCE)}`);
            if (isIzolifeSource(data.SOURCE)) {
              const izoResult = calcIzolifePrice(data, name, requiredPackages, priceValue, cache);
              const effectivePrice = izoResult.pricePerPackageWithDelivery;
              const analogPricePerSquareMeter = area > 0 ? effectivePrice / area : 0;
              const priceDifferencePerSquareMeter = isCurrentPriceOnRequest ? 0 : analogPricePerSquareMeter - currentItem.pricePerSquareMeter;
              const analogPricePerPackage = effectivePrice;
              const priceDifferencePerPackage = isCurrentPriceOnRequest ? 0 : analogPricePerPackage - currentItem.pricePerPackage;
              const analogPricePerSheet = sheetsInsulation > 0 ? effectivePrice / sheetsInsulation : Infinity;
              const priceDifferencePerSheet = isCurrentPriceOnRequest ? 0 : analogPricePerSheet - currentItem.pricePerSheet;
              const totalCost = effectivePrice * izoResult.actualPackages;
              const currentTotalCost = currentItem.pricePerPackage * parsedQuantity;
              const volumeBenefit = isCurrentPriceOnRequest ? -1 : totalCost - currentTotalCost;
              return {
                value: name,
                label: name,
                details: {
                  price: String(Math.round(effectivePrice)),
                  area,
                  density: data.DENSITY || "0.00",
                  insulationThickness: data.INSULATION_THICKNESS || "0.00",
                  category: data.CATEGORY || "-",
                  sheetsInsulation: sheetsInsulation || "Нет данных",
                  manufacturer: data.MANUFACTURER || "Не указан",
                  compression: data.COMPRESSION || null,
                },
                requiredPackages: izoResult.actualPackages,
                pricePerPackage: effectivePrice,
                analogPricePerPackage,
                priceDifferencePerPackage,
                analogPricePerSquareMeter,
                priceDifferencePerSquareMeter,
                analogPricePerSheet,
                priceDifferencePerSheet,
                volumeBenefit,
                totalCost,
                isIzolife: true,
                izolifeComment: izoResult.comment,
                izolifePhrase: izoResult.phrase,
              };
            }

            const analogPricePerSquareMeter = priceValue / area;
            const priceDifferencePerSquareMeter = isCurrentPriceOnRequest
              ? 0
              : analogPricePerSquareMeter - currentItem.pricePerSquareMeter;
            const analogPricePerPackage = priceValue;
            const priceDifferencePerPackage = isCurrentPriceOnRequest
              ? 0
              : analogPricePerPackage - currentItem.pricePerPackage;
            const analogPricePerSheet = sheetsInsulation > 0 ? priceValue / sheetsInsulation : Infinity;
            const priceDifferencePerSheet = isCurrentPriceOnRequest
              ? 0
              : analogPricePerSheet - currentItem.pricePerSheet;
            const analogSquareMeters = analogPricePerSquareMeter * (requiredPackages * area);
            const currentSquareMetersCost = currentItem.pricePerSquareMeter * currentItem.requiredVolume;
            const volumeBenefit = isCurrentPriceOnRequest
              ? -1
              : analogSquareMeters - currentSquareMetersCost;
            const totalCost = priceValue * requiredPackages;

            return {
              value: name,
              label: name,
              details: {
                price,
                area,
                density: data.DENSITY || "0.00",
                insulationThickness: data.INSULATION_THICKNESS || "0.00",
                category: data.CATEGORY || "-",
                sheetsInsulation: sheetsInsulation || "Нет данных",
                manufacturer: data.MANUFACTURER || "Не указан",
                compression: data.COMPRESSION || null,
              },
              requiredPackages,
              pricePerPackage: priceValue,
              analogPricePerPackage,
              priceDifferencePerPackage,
              analogPricePerSquareMeter,
              priceDifferencePerSquareMeter,
              analogPricePerSheet,
              priceDifferencePerSheet,
              volumeBenefit,
              totalCost,
            };
          })
          .filter((analog) => {
            if (isCurrentPriceOnRequest) {
              return analog !== null;
            }
            return analog && analog.volumeBenefit < 0;
          })
          .sort((a, b) => {
            if (isCurrentPriceOnRequest) {
              return a.totalCost - b.totalCost;
            }
            return a.volumeBenefit - b.volumeBenefit;
          })
          ;

        // Товары "по запросу" для XPS
        analogsOnRequest = withoutPrice
          .map(([name, data]) => {
            const area = parseFloat(String(data.AREA_SQUARE)?.replace(",", ".") || 0);
            const sheetsInsulation = parseInt(data.SHEETS_INSULATION || 0);
            const requiredPackages = Math.ceil(currentItem.requiredVolume / area);

            return {
              value: name,
              label: name,
              details: {
                price: null,
                area,
                density: data.DENSITY || "0.00",
                insulationThickness: data.INSULATION_THICKNESS || "0.00",
                category: data.CATEGORY || "-",
                sheetsInsulation: sheetsInsulation || "Нет данных",
                manufacturer: data.MANUFACTURER || "Не указан",
                compression: data.COMPRESSION || null,
              },
              requiredPackages,
              isPriceOnRequest: true,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label, 'ru'))
          ;
      } else if (isUteplitel) {
        const uteplitelFiltered = Object.entries(filteredOptions)
          .filter(([name, data]) => {
            if (name === productName) return false;
            const density = parseFloat(String(data.DENSITY)?.replace(",", ".") || 0);
            const insulationThickness = parseFloat(data.INSULATION_THICKNESS || 0);
            const category = data.CATEGORY || "-";
            const analogTypeMaterial = data.TYPE_MATERIAL || null;
            const isSameInsulationThickness =
              insulationThickness > 0 &&
              Math.abs(insulationThickness - currentInsulationThickness) < 0.01;
            const isUteplitelCategory = category.includes("Утеплитель") || category.includes("Звукоизоляция") || category.includes("Экструдированный пенополистирол");
            const isDensityInRange = density >= currentDensity - densityRange && density <= currentDensity + densityRange;
            const isIzolife = isIzolifeSource(data.SOURCE);
            // Сравниваем применения по справочнику, а не строкой: написания в
            // колонке разъезжаются, а аналоги от этого пропадать не должны.
            const hasMatchingAnalogy = shareApplication(currentAnalogy, data.ANALOGY);

            const isGlassAnalog = analogTypeMaterial === "стекловолокно";

            const isNameMatch = name.toLowerCase().includes("утеплитель") || category.includes("Утеплитель") || category.includes("Звукоизоляция");
            const isNotXps = !name.toLowerCase().includes("экструдированный пенополистирол");

            if (isGlassAnalog) {
              // Стекловолокно: только по применениям, толщина не важна
              return isNameMatch && isNotXps && isUteplitelCategory && hasMatchingAnalogy;
            }

            // Базальтовая вата и null — старые правила (плотность + толщина + применения)
            return isNameMatch && isNotXps && isSameInsulationThickness && isUteplitelCategory &&
              isDensityInRange && (isIzolife || hasMatchingAnalogy);
          });

        // Разделяем на товары с ценой и без
        const withPrice = uteplitelFiltered.filter(([name, data]) => {
          const price = paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH;
          return parseFloat(price) > 0;
        });
        const withoutPrice = uteplitelFiltered.filter(([name, data]) => {
          const price = paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH;
          return !price || parseFloat(price) <= 0;
        });

        console.log(`🟡 [Изолайф Products Утеплитель] аналогов с ценой: ${withPrice.length}, из них Изолайф: ${withPrice.filter(([n, d]) => isIzolifeSource(d.SOURCE)).length}`);

        analogs = withPrice
          .map(([name, data]) => {
            const area = parseFloat(String(data.AREA_SQUARE)?.replace(",", ".") || 0);
            const sheetsInsulation = parseInt(data.SHEETS_INSULATION || 0);
            const price =
              paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH;
            const priceValue = price ? parseFloat(price) || 0 : 0;
            const requiredPackages = Math.ceil(currentItem.requiredVolume / area);

            console.log(`🟡 [Изолайф Products] аналог "${name}": SOURCE="${data.SOURCE}", isIzolife=${isIzolifeSource(data.SOURCE)}`);
            if (isIzolifeSource(data.SOURCE)) {
              const izoResult = calcIzolifePrice(data, name, requiredPackages, priceValue, cache);
              const effectivePrice = izoResult.pricePerPackageWithDelivery;
              const analogPricePerSquareMeter = area > 0 ? effectivePrice / area : 0;
              const priceDifferencePerSquareMeter = isCurrentPriceOnRequest ? 0 : analogPricePerSquareMeter - currentItem.pricePerSquareMeter;
              const analogPricePerPackage = effectivePrice;
              const priceDifferencePerPackage = isCurrentPriceOnRequest ? 0 : analogPricePerPackage - currentItem.pricePerPackage;
              const analogPricePerSheet = sheetsInsulation > 0 ? effectivePrice / sheetsInsulation : Infinity;
              const priceDifferencePerSheet = isCurrentPriceOnRequest ? 0 : analogPricePerSheet - currentItem.pricePerSheet;
              const totalCost = effectivePrice * izoResult.actualPackages;
              const currentTotalCost = currentItem.pricePerPackage * parsedQuantity;
              const volumeBenefit = isCurrentPriceOnRequest ? -1 : totalCost - currentTotalCost;
              return {
                value: name,
                label: name,
                details: {
                  price: String(Math.round(effectivePrice)),
                  area,
                  density: data.DENSITY || "0.00",
                  insulationThickness: data.INSULATION_THICKNESS || "0.00",
                  category: data.CATEGORY || "-",
                  sheetsInsulation: sheetsInsulation || "Нет данных",
                  manufacturer: data.MANUFACTURER || "Не указан",
                  compression: data.COMPRESSION || null,
                  typeMaterial: data.TYPE_MATERIAL || null,
                },
                requiredPackages: izoResult.actualPackages,
                pricePerPackage: effectivePrice,
                analogPricePerPackage,
                priceDifferencePerPackage,
                analogPricePerSquareMeter,
                priceDifferencePerSquareMeter,
                analogPricePerSheet,
                priceDifferencePerSheet,
                volumeBenefit,
                totalCost,
                isIzolife: true,
                izolifeComment: izoResult.comment,
                izolifePhrase: izoResult.phrase,
              };
            }

            const analogPricePerSquareMeter = priceValue / area;
            const priceDifferencePerSquareMeter = isCurrentPriceOnRequest
              ? 0
              : analogPricePerSquareMeter - currentItem.pricePerSquareMeter;
            const analogPricePerPackage = priceValue;
            const priceDifferencePerPackage = isCurrentPriceOnRequest
              ? 0
              : analogPricePerPackage - currentItem.pricePerPackage;
            const analogPricePerSheet = sheetsInsulation > 0 ? priceValue / sheetsInsulation : Infinity;
            const priceDifferencePerSheet = isCurrentPriceOnRequest
              ? 0
              : analogPricePerSheet - currentItem.pricePerSheet;
            const analogSquareMeters = analogPricePerSquareMeter * (requiredPackages * area);
            const currentSquareMetersCost = currentItem.pricePerSquareMeter * currentItem.requiredVolume;
            const volumeBenefit = isCurrentPriceOnRequest
              ? -1
              : analogSquareMeters - currentSquareMetersCost;
            const totalCost = priceValue * requiredPackages;

            return {
              value: name,
              label: name,
              details: {
                price,
                area,
                density: data.DENSITY || "0.00",
                insulationThickness: data.INSULATION_THICKNESS || "0.00",
                category: data.CATEGORY || "-",
                sheetsInsulation: sheetsInsulation || "Нет данных",
                manufacturer: data.MANUFACTURER || "Не указан",
                compression: data.COMPRESSION || null,
                typeMaterial: data.TYPE_MATERIAL || null,
              },
              requiredPackages,
              pricePerPackage: priceValue,
              analogPricePerPackage,
              priceDifferencePerPackage,
              analogPricePerSquareMeter,
              priceDifferencePerSquareMeter,
              analogPricePerSheet,
              priceDifferencePerSheet,
              volumeBenefit,
              totalCost,
            };
          })
          .filter((analog) => {
            if (isCurrentPriceOnRequest) {
              return analog !== null;
            }
            return analog && analog.volumeBenefit < 0;
          })
          .sort((a, b) => {
            if (isCurrentPriceOnRequest) {
              return a.totalCost - b.totalCost;
            }
            return a.volumeBenefit - b.volumeBenefit;
          })
          ;

        // Товары "по запросу" для утеплителя
        analogsOnRequest = withoutPrice
          .map(([name, data]) => {
            const area = parseFloat(String(data.AREA_SQUARE)?.replace(",", ".") || 0);
            const sheetsInsulation = parseInt(data.SHEETS_INSULATION || 0);
            const requiredPackages = Math.ceil(currentItem.requiredVolume / area);

            return {
              value: name,
              label: name,
              details: {
                price: null,
                area,
                density: data.DENSITY || "0.00",
                insulationThickness: data.INSULATION_THICKNESS || "0.00",
                category: data.CATEGORY || "-",
                sheetsInsulation: sheetsInsulation || "Нет данных",
                manufacturer: data.MANUFACTURER || "Не указан",
                compression: data.COMPRESSION || null,
                typeMaterial: data.TYPE_MATERIAL || null,
              },
              requiredPackages,
              isPriceOnRequest: true,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label, 'ru'))
          ;
      } else if (isBrick) {
        // Аналог кирпича подбирается по трём характеристикам из справочника guide:
        //  - НФ (FORMAT) — должно совпадать (размер и НФ эквивалентны, 1НФ = 250х120х65);
        //  - пустотность (DENSITY_REPEAT) — должна совпадать;
        //  - прочность (STRENGTH, грейд М###) — не ниже текущей.
        // Асимметрия по прочности: более высокая прочность допустима и выводится,
        // если аналог дешевле; более низкая прочность — никогда.
        // Единица кирпича — штука, цена сравнивается за штуку.
        const brickFiltered = Object.entries(options).filter(([name, data]) => {
          if (name === productName) return false;
          if (!isBrickCategory(data.CATEGORY)) return false;

          const format = normalizeBrickAttr(data.FORMAT);
          const voidness = normalizeBrickAttr(data.DENSITY_REPEAT);
          const strengthGrade = parseStrengthGrade(data.STRENGTH);

          // НФ и пустотность обязаны быть заданы и совпадать
          if (!currentBrickFormat || !format || format !== currentBrickFormat) return false;
          if (!currentBrickVoidness || !voidness || voidness !== currentBrickVoidness) return false;
          // Прочность не ниже текущей
          if (!currentBrickStrengthGrade || !strengthGrade || strengthGrade < currentBrickStrengthGrade) return false;

          return true;
        });

        const withPrice = brickFiltered.filter(([_, data]) => {
          const price = paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH;
          return parseFloat(price) > 0;
        });
        const withoutPrice = brickFiltered.filter(([_, data]) => {
          const price = paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH;
          return !price || parseFloat(price) <= 0;
        });

        const buildBrickDetails = (data) => ({
          price: paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH,
          category: data.CATEGORY || "-",
          manufacturer: data.MANUFACTURER || "Не указан",
          brickFormat: data.FORMAT || null,
          brickVoidness: data.DENSITY_REPEAT || null,
          brickStrength: data.STRENGTH || null,
        });

        analogs = withPrice
          .map(([name, data]) => {
            const price = paymentMethod === "cash" ? data.PRICE_CASH : data.PRICE_NONCASH;
            const priceValue = price ? parseFloat(price) || 0 : 0;
            const priceDifferencePerPackage = isCurrentPriceOnRequest ? 0 : priceValue - currentItem.pricePerPackage;
            const strengthGrade = parseStrengthGrade(data.STRENGTH);

            return {
              value: name,
              label: name,
              details: buildBrickDetails(data),
              requiredSheets: parsedQuantity,
              analogPricePerPackage: priceValue,
              priceDifferencePerPackage,
              volumeBenefit: priceDifferencePerPackage,
              totalCost: priceValue * parsedQuantity,
              isBrickAnalog: true,
              isHigherStrength: currentBrickStrengthGrade > 0 && strengthGrade > currentBrickStrengthGrade,
            };
          })
          // Не фильтруем и не сортируем по цене прайса здесь: у кирпича она без доставки
          // ничего не говорит о том, что реально дешевле — производство подешевле по
          // прайсу может стоять в 3 раза дальше и быть дороже в сумме. Финальные
          // filter+sort по цене С доставкой делает AnalogsList.js, когда доставка по
          // всем кандидатам посчитана.
          .sort((a, b) => a.analogPricePerPackage - b.analogPricePerPackage);

        analogsOnRequest = withoutPrice
          .map(([name, data]) => ({
            value: name,
            label: name,
            details: { ...buildBrickDetails(data), price: null },
            requiredSheets: parsedQuantity,
            isPriceOnRequest: true,
            isBrickAnalog: true,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
      }

      const result = { currentItem, analogs, analogsOnRequest };
      analogsCache.current[fullCacheKey] = result;
      return result;
    },
    [options, filteredOptions, paymentMethod, products, cache]
  );

  return { getAnalogs };
};

export default useAnalogs;
